/*----------------------------------
- COMPONENT: PUBLIC DOMAINS TABLE CELL
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 *
 * One resolved cell, drawn as the mock's `cell()` draws it (website-domains.html): the status as the kit `Chip`,
 * a price as the kit `Price`, a reading in its tone (`dm-t--<tone>`), est. profit on its three-marker track
 * (`dm-up`), TLD coverage as three dots (`dm-cov`), liquidity as the kit `Bar`, similar sales with its tier in
 * `dm-sub`, and "–" for a cell with no reading. The table and the phone card both draw through it, so the two
 * presentations cannot disagree about a figure. The rating is not a cell kind here: the table and the card draw
 * the kit `Rating` from `resolvePublicRating`.
 *
 * Nothing is computed: every figure and every marker position is the shared resolver's (`cellValues.ts`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Bar } from '@uniquedomains/components/kit/bar/Bar';
import { Chip, statusTone } from '@uniquedomains/components/kit/chip/Chip';
import { Price } from '@uniquedomains/components/kit/price/Price';
import Icon from '@/client/components/ui/icon/Icon';
import { PUBLIC_PREVIEW_TABLE_COPY } from '@/client/catalogs/landing/domains/publicPreviewCopy';

// Types
import type { TableCellValue } from '@uniquedomains/components/domains/table/cellValues';

/*----------------------------------
- TYPES
----------------------------------*/

type PublicDomainsTableCellProps = {
    value: TableCellValue;
    /** The row's status id, which picks the status chip's tone (the resolver gives its label only). */
    status?: string | null;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const NONE = <span class="dm-none">–</span>;

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsTableCell = ({ value, status = null }: PublicDomainsTableCellProps) => {
    switch (value.kind) {
        case 'empty':
            return NONE;
        case 'chip':
            return <Chip tone={statusTone(status)} label={value.value} />;
        case 'text':
            // A figure the row leads with (the price) is the kit's price; a renewal or a note keeps its tone.
            return value.tone === 'ink' ? <Price value={value.value} /> : <span class={`dm-t--${value.tone}`}>{value.value}</span>;
        case 'timing':
            return <span class={`dm-t--${value.tone}`}>{value.value}</span>;
        case 'fingerprint':
            return (
                <>
                    {value.value}
                    {value.warn ? (
                        <span role="img" aria-label={PUBLIC_PREVIEW_TABLE_COPY.spellingRisk}>
                            <Icon name="alert" size={14} strokeWidth={2.2} class="dm-warn" />
                        </span>
                    ) : null}
                </>
            );
        case 'upside':
            return (
                <span class="dm-up">
                    {value.markers ? (
                        <span class="dm-up__track" aria-hidden="true">
                            {value.markers.medianPercent === null ? null : (
                                <i class="dm-mk dm-mk--median" style={{ left: `${value.markers.medianPercent}%` }} />
                            )}
                            <i class="dm-mk dm-mk--estimate" style={{ left: `${value.markers.estimatePercent}%` }} />
                            <i class="dm-mk dm-mk--price" style={{ left: `${value.markers.pricePercent}%` }} />
                        </span>
                    ) : null}
                    <b class={`dm-t--${value.tone}`}>{value.value}</b>
                </span>
            );
        case 'sales':
            return (
                <>
                    {value.value} <span class="dm-sub">· {value.tierLabel}</span>
                </>
            );
        case 'coverage':
            return (
                <span class="dm-cov">
                    {value.items.map((item) => (
                        <span key={item.id}>
                            <i class={`dm-dot dm-dot--${item.id}`} aria-hidden="true" />
                            {item.value ?? 0}
                        </span>
                    ))}
                </span>
            );
        case 'bar':
            return (
                <span class="dm-liq">
                    <Bar pct={value.percent} />
                    {value.value}
                </span>
            );
        case 'signals':
            return (
                <span class="dm-sigs">
                    {value.signals.map((signal) => <Chip key={signal.type} label={signal.label} title={signal.description ?? undefined} />)}
                    {value.overflow.length > 0 ? <span class="dm-sub">+{value.overflow.length}</span> : null}
                </span>
            );
        case 'stars':
            // The public table draws the rating from `resolvePublicRating`; a stars value never reaches here.
            return NONE;
        default:
            return <span>{value.value}</span>;
    }
};

export default PublicDomainsTableCell;
